// ── SEO-friendly show URLs ────────────────────────────────────────────────────
// `/show/1396-breaking-bad` instead of `/show/1396`.
//
// ID-FIRST HYBRID, deliberately (the Reddit / Stack Overflow shape). The numeric TMDB
// id stays the source of truth and the slug is decoration the router ignores:
//
//   • Nothing resolves BY slug, so there is no slug column, no uniqueness problem, no
//     collision between two shows with the same name, and a renamed show needs no
//     migration or redirect.
//   • Every `/show/1396` link already shared — texts, Discord, unfurl cards, push
//     notifications — keeps working forever, unchanged. No redirect maze.
//   • The canonical URL is the slugged one, so search engines converge on a single
//     form rather than indexing both.
//
// The slug is cosmetic for ranking purposes; its real value is a legible link in a
// text message and a readable result in a SERP. It is NOT a substitute for the
// prerendering work — a pretty URL on a page a crawler can't render gains little.

// TMDB titles carry accents, colons, apostrophes and the occasional emoji.
export function slugify(name) {
  if (!name || typeof name !== 'string') return ''
  return name
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')  // strip diacritics: Pokémon → Pokemon
    .toLowerCase()
    .replace(/['’]/g, '')          // don't turn "Bob's" into "bob-s"
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)                  // keep URLs sane; the id already guarantees identity
    .replace(/-+$/g, '')           // slice() can leave a trailing hyphen
}

// Build the path. Falls back to the bare id when the name is unknown (a room reached
// before TMDB answers), which is still a valid, working URL.
export function showPath(id, name) {
  const s = slugify(name)
  return s ? `/show/${id}-${s}` : `/show/${id}`
}

export function aftershowPath(id, name) {
  const s = slugify(name)
  return s ? `/aftershow/${id}-${s}` : `/aftershow/${id}`
}

// Parse the leading integer and ignore everything after it. This is the ONLY thing the
// router should ever trust.
//
// parseInt, NOT Number: Number('1396-breaking-bad') is NaN, which is precisely the bug
// that would make every slugged URL a blank page. parseInt stops at the first
// non-digit and returns 1396.
export function parseShowId(param) {
  if (param == null) return NaN
  const n = parseInt(String(param), 10)
  return Number.isFinite(n) && n > 0 ? n : NaN
}
